import { colorCharts } from "data/palettes";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import styles from "./generic.module.scss";

const renderCustomBarLabel = (props) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const { x, y, width, height, value, index } = props;

  const textContent = `${value.toFixed(2)}%`;
  const textWidth = textContent.length * 8;
  const padding = 0;
  const rectWidth = textWidth + padding * 2;

  const backgroundColor =
    index % 2 === 0
      ? colorsConfig.productCardBarChartLabel2
      : colorsConfig.productCardBarChartLabel1;

  return (
    <g>
      <rect
        x={x + width + 7}
        y={y + height / 2 - 12}
        width={rectWidth}
        height={23}
        fill={backgroundColor}
        rx={5}
      />
      <text
        x={x + width + 12}
        y={y + height / 2 + 0}
        fill={colorsConfig.FinecoBlack}
        dominantBaseline="middle"
        textAnchor="start"
        fontSize={12}
        fontWeight={700}
        fontFamily="Proxima Nova"
      >
        {`${value.toFixed(2)}%`}
      </text>
    </g>
  );
};

const CustomYAxisTick = (props) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const { x, y, payload } = props;

  const truncateText = (text, maxLength = 28) => {
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={-10}
        y={0}
        dy={4}
        textAnchor="end"
        fill={colorsConfig.FinecoBlack}
        fontSize={14}
        fontFamily="Proxima Nova"
        fontWeight={400}
        className="yaxis-label"
      >
        {truncateText(payload.value)}
      </text>
    </g>
  );
};

const CustomXAxisTick = (props) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const { x, y, payload } = props;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill={colorsConfig.FinecoBlack}
        fontSize={12}
        fontFamily="Proxima Nova"
        fontWeight={400}
        className="xaxis-label"
      >
        {`${payload.value}%`}
      </text>
    </g>
  );
};

// Improved tick calculation that handles all-zero datasets
const calculateNiceTicks = (maxValue) => {
  // If all values are zero, create a default scale
  if (maxValue === 0) {
    return [0, 25, 50, 75, 100];
  }

  // Round up to the next 5
  const roundedMax = Math.ceil(maxValue / 5) * 5;

  // Ensure we have a minimum scale even for very small values
  const effectiveMax = Math.max(roundedMax, 5);

  // Calculate a nice step size (divide by 4 to get 5 ticks including 0)
  const step = effectiveMax / 4;

  // Generate the ticks
  return Array.from({ length: 5 }, (_, i) => Number((i * step).toFixed(1)));
};

export const GenericChart = ({ data, title }) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);

  const maxWeight = Math.max(...data.map((item) => item.weight));
  const ticks = calculateNiceTicks(maxWeight);

  const formattedData = data.map((item, index) => ({
    ...item,
    weight: parseFloat(item.weight.toFixed(2)),
    fill: colorsConfig[colorCharts[index % colorCharts.length]],
  }));

  const chartHeight = formattedData.length * 40 + 54;

  return (
    <div>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={chartHeight} debounce={500}>
          <BarChart
            data={formattedData}
            layout="vertical"
            margin={{ top: 15, right: 80, left: 120, bottom: 15 }}
            barGap={30}
          >
            <CartesianGrid horizontal={false} vertical={false} />
            <XAxis
              type="number"
              domain={[0, ticks[ticks.length - 1]]}
              ticks={ticks}
              tick={<CustomXAxisTick />}
              tickLine={false}
              axisLine={{ stroke: colorsConfig.Gray3 }}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={100}
              interval={0}
              tick={<CustomYAxisTick />}
              axisLine={{ stroke: colorsConfig.Gray3 }}
              tickLine={false}
            />
            <Bar
              dataKey="weight"
              radius={[0, 4, 4, 0]}
              barSize={26}
              minPointSize={2}
              isAnimationActive={false}
            >
              <LabelList
                dataKey="weight"
                position="right"
                content={renderCustomBarLabel}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const GenericContainer = ({ data, title }) => {
  return (
    <div className={styles.container}>
      <GenericChart data={data} title={title} />
    </div>
  );
};

export default GenericContainer;
